import React, {useState, useEffect} from 'react'
import {CourseCertificate, CourseCertificateResponse} from '../../core/_models'
import {
  uploadCourseCertificateFile,
  fetchCourseCertificateFileBlob,
  deleteCourseCertificateFile,
  createCourseCertificate,
  updateCourseCertificate,
  getOnboardingToken,
  saveCrewOnboardingCourses,
  getCrewOnboardingDraft,
} from '../../core/_requests'
import {toast} from 'react-toastify'
import {showErrorToast} from '../../../../shared/utils/apiErrorMessage'
import {DateTextInput} from '../DateTextInput'
import {useAuth} from '../../../auth'
interface CoursesCertificatesProps {
  crewId: number | null
  courseCertificates: CourseCertificate[]
  handleCourseCertificateChange: (
    index: number,
    field: keyof CourseCertificate,
    value: string
  ) => void
  handleAddCourseCertificateRow: (category: string) => void
  handleRemoveCourseCertificateRow: (index: number) => void
  fieldErrors: Record<string, string>
  existingCourseCertificates: CourseCertificateResponse[]
  onRefresh?: () => void | Promise<void>
}
const courseCategories = [
  {
    label: 'STCW Certificates',
    courses: [
      'Personal Survival & Social Responsibility (PSSR)',
      'Proficiency in Survival Craft & Rescue Boat (PSCRB)',
      'Proficiency in Survival Technique (PST)',
      'Advanced Fire Fighting (AFF)/ Fire Prevention and Fire Fighting (FPFF)',
      'Elementary First Aid (EFA)/ Medical First Aid (MFA)/ Medicare',
      'STSDSD / SSO Course',
      'High Voltage Training',
      'Radar Observer / ARPA',
      'Radar Simulator (RANSCO) / ENS',
      'LCHS',
      'Ship Safety Officer',
    ],
  },
  {
    label: 'Tanker Courses',
    courses: [
      'Oil Tanker Familiarization (OTFC)/Advance Training for Cargo operation(TASCO)',
      'Chemical Tanker Familiarization (CTFC)',
      'Gas Familiarization (GTFC)',
      'Chemical Tanker Safety (CHEMCO)',
      'Gas Tanker Safety (GASCO)',
    ],
  },
  {
    label: 'Navigation & Main Engine Training',
    courses: [
      'Bridge Team Management (BTM)/Bridge Resource Management (BRM)',
      'Engine Room Simulator (ERS)',
      'ECDIS-Generic',
      'ECDIS-Type Specific',
      'Any Value Added Course/Company Specific Course',
      'Yellow Fever',
    ],
  },
  {
    label: 'Revalidation Course',
    courses: [
      'Refresher and Updating Training (Up gradation course) for Deck Officers',
      'Refresher and Updating Training for Engineer Officers',
      'Add. Training if any',
    ],
  },
  {
    label: 'Value Added Course',
    courses: [],
  },
]
const MAX_FILE_SIZE = 9 * 1024 * 1024 // 9 MB
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf']
export const getCategoryForCourse = (courseName: string): string => {
  const category = courseCategories.find((cat) => cat.courses.includes(courseName))
  return category ? category.label : 'Revalidation Course'
}
export const buildCourseCertificateState = (source: any[] = []): any[] => {
  const courseCertificateDetails = courseCategories.flatMap((cat) => cat.courses)
  const normalizedSource = source.map((certificate) => {
    const certificateName = (certificate.certificateName || '').trim()
    const category =
      certificate.category ||
      (certificateName ? getCategoryForCourse(certificateName) : 'Revalidation Course')
    return {
      id: certificate.id,
      crewId: certificate.crewId,
      certificateName,
      category,
      certificateNumber: certificate.certificateNumber || '',
      dateOfIssue: certificate.dateOfIssue || '',
      dateOfExpiry: certificate.dateOfExpiry || '',
      issuedBy: certificate.issuedBy || '',
      filePath: certificate.filePath || null,
      fileName: certificate.fileName || null,
      uploadedAt: certificate.uploadedAt || null,
    }
  })
  const defaultRows = courseCertificateDetails.map((certificateName) => {
    const existingCert = normalizedSource.find((cert) => cert.certificateName === certificateName)
    if (existingCert) {
      return existingCert
    }
    return {
      certificateName,
      category: getCategoryForCourse(certificateName),
      certificateNumber: '',
      dateOfIssue: '',
      dateOfExpiry: '',
      issuedBy: '',
      filePath: null,
      fileName: null,
      uploadedAt: null,
    }
  })
  const customRows = normalizedSource.filter((certificate) => {
    if (!certificate.certificateName) {
      return true
    }
    return !courseCertificateDetails.includes(certificate.certificateName)
  })
  return [...defaultRows, ...customRows]
}
const CoursesCertificates: React.FC<CoursesCertificatesProps> = ({
  crewId,
  courseCertificates,
  handleCourseCertificateChange,
  handleAddCourseCertificateRow,
  handleRemoveCourseCertificateRow,
  fieldErrors,
  existingCourseCertificates,
  onRefresh,
}) => {
  const {isSuperAdmin, hasPermission, hasAnyPermission} = useAuth()
  const isOnboarding = !!getOnboardingToken()
  const canUploadCrewDocument =
    isSuperAdmin || hasPermission('UPLOAD_CREW_DOCUMENT') || isOnboarding
  const canDeleteCrewDocument =
    isSuperAdmin || hasPermission('DELETE_CREW_DOCUMENT') || isOnboarding
  const canViewCrewDocumentFile =
    isSuperAdmin || hasPermission('READ_CREW_DOCUMENT') || isOnboarding
  const [filesByRow, setFilesByRow] = useState<Record<string, File | null>>({})
  const [idByRow, setIdByRow] = useState<Record<string, number | undefined>>({})
  const [hasFileByRow, setHasFileByRow] = useState<Record<string, boolean>>({})
  const [removedFileIds, setRemovedFileIds] = useState<Set<number>>(new Set())
  useEffect(() => setRemovedFileIds(new Set()), [crewId])
  const getRowKey = (rowIndex: number, certificate: CourseCertificate) =>
    certificate.id ? `id_${certificate.id}` : `row_${rowIndex}`
  const validateFile = (file: File | null) => {
    if (!file) return {isValid: false, message: 'No file selected'}
    if (!ACCEPTED_TYPES.includes(file.type)) {
      return {
        isValid: false,
        message: 'Invalid file type. Only JPEG, PNG, JPG, and PDF are allowed.',
      }
    }
    if (file.size > MAX_FILE_SIZE) {
      return {
        isValid: false,
        message: 'File is too large. Maximum size is 9MB.',
      }
    }
    return {isValid: true, message: ''}
  }
  // Initialize row meta from backend on load / change
  useEffect(() => {
    const nextIds: Record<string, number | undefined> = {}
    const nextHasFile: Record<string, boolean> = {}
    courseCertificates.forEach((certificate, rowIndex) => {
      const rowKey = getRowKey(rowIndex, certificate)
      const matchById =
        certificate.id !== undefined
          ? existingCourseCertificates.find((item) => item.id === certificate.id)
          : undefined
      const matchByNameAndCategory = existingCourseCertificates.find(
        (item) =>
          item.certificateName === certificate.certificateName &&
          (item.category || '') === (certificate.category || '')
      )
      const matched = matchById || matchByNameAndCategory
      if (matched?.id) {
        nextIds[rowKey] = matched.id
        nextHasFile[rowKey] = !!matched.filePath
      } else if (certificate.id) {
        nextIds[rowKey] = certificate.id
        nextHasFile[rowKey] = !!certificate.filePath
      }
    })
    setIdByRow(nextIds)
    setHasFileByRow(nextHasFile)
  }, [existingCourseCertificates, courseCertificates])
  const handleFileChangeLocal = (
    rowIndex: number,
    certificate: CourseCertificate,
    file: File | null
  ) => {
    if (file) {
      const validation = validateFile(file)
      if (!validation.isValid) {
        toast.warn(validation.message)
        return
      }
    }
    const rowKey = getRowKey(rowIndex, certificate)
    setFilesByRow((prev) => ({...prev, [rowKey]: file}))
  }
  const handleSaveFile = async (rowIndex: number, certificate: CourseCertificate) => {
    const rowKey = getRowKey(rowIndex, certificate)
    const file = filesByRow[rowKey]
    const certificateName = (certificate.certificateName || '').trim()
    if (!file) {
      toast.warn('Please choose a file before saving.')
      return
    }
    if (!certificateName) {
      toast.warn('Please enter Course Name before saving file.')
      return
    }
    if (!crewId && !isOnboarding) {
      toast.warn('Please save Personal Data (crew) first.')
      return
    }
    try {
      let courseId = certificate.id || idByRow[rowKey]
      if (isOnboarding && !courseId) {
        const token = getOnboardingToken()
        if (token) {
          await saveCrewOnboardingCourses(token, courseCertificates)
          const draft = await getCrewOnboardingDraft(token)
          if (draft && draft.courses) {
            const matched = draft.courses.find(
              (c: any) =>
                c.certificateName === certificateName &&
                (c.category || '') === (certificate.category || '')
            )
            if (matched) {
              courseId = matched.id
              if (onRefresh) {
                await onRefresh()
              }
            }
          }
        }
      }
      const payload: CourseCertificate = {
        certificateName,
        category: certificate.category || 'Revalidation Course',
        certificateNumber: certificate.certificateNumber || null,
        dateOfIssue: certificate.dateOfIssue || null,
        dateOfExpiry: certificate.dateOfExpiry || null,
        issuedBy: certificate.issuedBy || null,
      }
      if (!courseId) {
        if (isOnboarding) {
          toast.warn('Could not resolve course ID. Please try saving details first.')
          return
        }
        const created = await createCourseCertificate(crewId, payload)
        courseId = created.id
      } else {
        if (isOnboarding) {
          // Details already saved via saveCrewOnboardingCourses above
        } else {
          await updateCourseCertificate(crewId, courseId, payload)
        }
      }
      await uploadCourseCertificateFile(courseId!, file)
      setRemovedFileIds((prev) => { const next = new Set(prev); next.delete(courseId!); return next })
      setIdByRow((prev) => ({...prev, [rowKey]: courseId}))
      setHasFileByRow((prev) => ({...prev, [rowKey]: true}))
      setFilesByRow((prev) => ({...prev, [rowKey]: null}))
      toast.success('Course certificate file uploaded.')
    } catch (err) {
      console.error('Failed to upload course certificate file', err)
      showErrorToast(err, 'Failed to upload course certificate file.')
    }
  }
  const handlePreviewFile = async (rowIndex: number, certificate: CourseCertificate) => {
    const rowKey = getRowKey(rowIndex, certificate)
    const id = certificate.id || idByRow[rowKey]
    const hasServerFile = !removedFileIds.has(id!) && (!!certificate.filePath || !!hasFileByRow[rowKey])
    if (!id || !hasServerFile) {
      toast.warn('No file available to preview for this course.')
      return
    }
    try {
      const blob = await fetchCourseCertificateFileBlob(id)
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch (err) {
      console.error('Failed to preview course certificate file', err)
      showErrorToast(err, 'Failed to preview file. It may not exist on server.')
    }
  }
  const handleDeleteFile = async (rowIndex: number, certificate: CourseCertificate) => {
    const rowKey = getRowKey(rowIndex, certificate)
    const id = certificate.id || idByRow[rowKey]
    const hasServerFile = !removedFileIds.has(id!) && (!!certificate.filePath || !!hasFileByRow[rowKey])
    if (!id || !hasServerFile) {
      setFilesByRow((prev) => ({...prev, [rowKey]: null}))
      setHasFileByRow((prev) => ({...prev, [rowKey]: false}))
      return
    }
    try {
      await deleteCourseCertificateFile(id)
      setRemovedFileIds((prev) => new Set(prev).add(id))
      setFilesByRow((prev) => ({...prev, [rowKey]: null}))
      setHasFileByRow((prev) => ({...prev, [rowKey]: false}))
      toast.success('Course certificate file removed.')
    } catch (err) {
      console.error('Failed to delete course certificate file', err)
      showErrorToast(err, 'Failed to delete file.')
    }
  }
  const renderFileCell = (rowIndex: number, certificate: CourseCertificate) => {
    const rowKey = getRowKey(rowIndex, certificate)
    const pendingFile = filesByRow[rowKey] ?? null
    const id = certificate.id || idByRow[rowKey]
    const hasServerFile = !removedFileIds.has(id!) && (!!certificate.filePath || !!hasFileByRow[rowKey])
    const fileInputId = `course-file-${rowKey}`
    // ✅ If file already exists on server → only show icons (View + Remove)
    if (hasServerFile) {
      return (
        <div className='d-flex flex-wrap gap-2 align-items-center'>
          {canViewCrewDocumentFile && (
            <button
              type='button'
              className='btn btn-sm btn-icon btn-info'
              title='View file'
              onClick={() => handlePreviewFile(rowIndex, certificate)}
            >
              <i className='bi bi-eye' />
            </button>
          )}
          {canDeleteCrewDocument && (
            <button
              type='button'
              className='btn btn-sm btn-icon btn-secondary'
              title='Remove file'
              onClick={() => handleDeleteFile(rowIndex, certificate)}
            >
              <i className='bi bi-trash' />
            </button>
          )}
          {!canViewCrewDocumentFile && !canDeleteCrewDocument && (
            <span className='text-success small'>Uploaded</span>
          )}
        </div>
      )
    }
    if (!canUploadCrewDocument) {
      return <span className='text-muted small'>Not uploaded</span>
    }
    // ✅ No file yet → show styled choose file + Upload
    return (
      <div className='d-flex align-items-center gap-2 flex-wrap'>
        <input
          id={fileInputId}
          type='file'
          className='d-none'
          accept='.pdf,.png,.jpg,.jpeg'
          onChange={(e) =>
            handleFileChangeLocal(rowIndex, certificate, e.target.files?.[0] || null)
          }
        />
        {!pendingFile ? (
          <label
            htmlFor={fileInputId}
            className='btn btn-light-primary mb-0'
            style={{
              height: '31px',
              minWidth: '102px',
              padding: '0 0.75rem',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.875rem',
              whiteSpace: 'nowrap',
              lineHeight: 1,
            }}
          >
            Choose File
          </label>
        ) : (
          <label
            htmlFor={fileInputId}
            className='text-primary small mb-0 text-truncate'
            style={{maxWidth: '200px', cursor: 'pointer'}}
            title={pendingFile.name}
          >
            {pendingFile.name}
          </label>
        )}
        {pendingFile && (
          <button
            type='button'
            className='btn btn-primary'
            style={{
              height: '31px',
              minWidth: '78px',
              padding: '0 0.75rem',
              fontSize: '0.875rem',
              whiteSpace: 'nowrap',
              lineHeight: 1,
            }}
            onClick={() => handleSaveFile(rowIndex, certificate)}
          >
            Upload
          </button>
        )}
      </div>
    )
  }
  return (
    <div className='container my-4'>
      <h5 className='fw-bold mb-4'>Courses &amp; Certificates</h5>
      {courseCategories.map((category, catIndex) => (
        <div key={catIndex} className='mb-4'>
          <h6 className='fw-bold mb-3'>{category.label}</h6>
          <div className='table-responsive'>
            <table className='table table-bordered'>
              <thead className='table-light'>
                <tr>
                  <th style={{width: '1%'}}></th>
                  <th>Course Name</th>
                  <th>Certificate Number</th>
                  <th>Date of Issue</th>
                  <th>Date of Expiry</th>
                  <th>Issued By</th>
                  <th>File</th>
                </tr>
              </thead>
              <tbody>
                {category.courses.map((courseName) => {
                  const certIndex = courseCertificates.findIndex(
                    (cert) => cert.certificateName === courseName
                  )
                  const certificate =
                    certIndex !== -1
                      ? courseCertificates[certIndex]
                      : {
                          certificateName: courseName,
                          category: category.label,
                          certificateNumber: '',
                          dateOfIssue: '',
                          dateOfExpiry: '',
                          issuedBy: '',
                        }
                  const globalIndex = certIndex
                  return (
                    <tr key={`${catIndex}-${courseName}`}>
                      <td></td>
                      <td>{courseName}</td>
                      <td>
                        <input
                          type='text'
                          className='form-control'
                          placeholder='Certificate Number'
                          value={certificate.certificateNumber || ''}
                          onChange={(e) =>
                            globalIndex >= 0 &&
                            handleCourseCertificateChange(
                              globalIndex,
                              'certificateNumber',
                              e.target.value
                            )
                          }
                        />
                        {fieldErrors[`course_certificateNumber_${globalIndex}`] && (
                          <div className='text-danger mt-1'>
                            {fieldErrors[`course_certificateNumber_${globalIndex}`]}
                          </div>
                        )}
                      </td>
                      <td>
                        <DateTextInput
                          className='form-control'
                          value={certificate.dateOfIssue || ''}
                          onChange={(val) =>
                            globalIndex >= 0 &&
                            handleCourseCertificateChange(globalIndex, 'dateOfIssue', val)
                          }
                        />
                        {fieldErrors[`course_dateOfIssue_${globalIndex}`] && (
                          <div className='text-danger mt-1'>
                            {fieldErrors[`course_dateOfIssue_${globalIndex}`]}
                          </div>
                        )}
                      </td>
                      <td>
                        <DateTextInput
                          className='form-control'
                          value={certificate.dateOfExpiry || ''}
                          onChange={(val) =>
                            globalIndex >= 0 &&
                            handleCourseCertificateChange(globalIndex, 'dateOfExpiry', val)
                          }
                        />
                        {fieldErrors[`course_dateOfExpiry_${globalIndex}`] && (
                          <div className='text-danger mt-1'>
                            {fieldErrors[`course_dateOfExpiry_${globalIndex}`]}
                          </div>
                        )}
                      </td>
                      <td>
                        <input
                          type='text'
                          className='form-control'
                          placeholder='Issued By'
                          value={certificate.issuedBy || ''}
                          onChange={(e) =>
                            globalIndex >= 0 &&
                            handleCourseCertificateChange(globalIndex, 'issuedBy', e.target.value)
                          }
                        />
                        {fieldErrors[`course_issuedBy_${globalIndex}`] && (
                          <div className='text-danger mt-1'>
                            {fieldErrors[`course_issuedBy_${globalIndex}`]}
                          </div>
                        )}
                      </td>
                      <td>{globalIndex >= 0 ? renderFileCell(globalIndex, certificate) : null}</td>
                    </tr>
                  )
                })}
                {courseCertificates
                  .map((certificate, index) => ({certificate, index}))
                  .filter(
                    ({certificate}) =>
                      (certificate.category || '') === category.label &&
                      !category.courses.includes((certificate.certificateName || '').trim())
                  )
                  .map(({certificate, index}) => (
                    <tr key={`custom-${catIndex}-${index}`}>
                      <td className='text-center align-middle'>
                        {(!certificate.id || canDeleteCrewDocument) && <button
                          type='button'
                          className='btn btn-sm btn-icon btn-light-danger'
                          title='Remove certificate row'
                          onClick={() => handleRemoveCourseCertificateRow(index)}
                        >
                          <i className='bi bi-x-lg' />
                        </button>}
                      </td>
                      <td>
                        <input
                          type='text'
                          className='form-control'
                          placeholder='Course Name'
                          value={certificate.certificateName || ''}
                          onChange={(e) =>
                            handleCourseCertificateChange(index, 'certificateName', e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          type='text'
                          className='form-control'
                          placeholder='Certificate Number'
                          value={certificate.certificateNumber || ''}
                          onChange={(e) =>
                            handleCourseCertificateChange(
                              index,
                              'certificateNumber',
                              e.target.value
                            )
                          }
                        />
                      </td>
                      <td>
                        <DateTextInput
                          className='form-control'
                          value={certificate.dateOfIssue || ''}
                          onChange={(val) =>
                            handleCourseCertificateChange(index, 'dateOfIssue', val)
                          }
                        />
                      </td>
                      <td>
                        <DateTextInput
                          className='form-control'
                          value={certificate.dateOfExpiry || ''}
                          onChange={(val) =>
                            handleCourseCertificateChange(index, 'dateOfExpiry', val)
                          }
                        />
                      </td>
                      <td>
                        <input
                          type='text'
                          className='form-control'
                          placeholder='Issued By'
                          value={certificate.issuedBy || ''}
                          onChange={(e) =>
                            handleCourseCertificateChange(index, 'issuedBy', e.target.value)
                          }
                        />
                      </td>
                      <td>{renderFileCell(index, certificate)}</td>
                    </tr>
                  ))}
                <tr>
                  <td></td>
                  <td>
                    <button
                      type='button'
                      className='btn btn-sm btn-light-primary'
                      onClick={() => handleAddCourseCertificateRow(category.label)}
                    >
                      <i className='bi bi-plus-lg me-1' /> Add Certificate
                    </button>
                  </td>
                  <td colSpan={5}></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  )
}
export default CoursesCertificates
